import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../ui/LanguageSwitcher';

const Header = ({ activeSection, setActiveSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: t('nav.home') },
    { id: 'gallery', label: t('nav.gallery') },
    { id: 'about', label: t('nav.about') },
    { id: 'exhibitions', label: t('nav.exhibitions') },
    { id: 'contacts', label: t('nav.contacts') },
  ];

  const handleNavClick = (id) => {
    setActiveSection(id);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 safe-area-inset-top ${
        scrolled
          ? 'bg-[#FAF7F2]/95 backdrop-blur-elegant shadow-sm py-3 xs:py-4'
          : 'bg-transparent py-4 xs:py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 xs:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <button
            onClick={() => handleNavClick('home')}
            className="group flex items-center space-x-3"
          >
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#B8963E]/30 group-hover:border-[#B8963E] transition-colors duration-300">
              <img
                src="/pics/ivp_logo.png"
                alt={t('header.artistName')}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-normal text-[#3D3831] tracking-wide">
                {t('header.artistName')}
              </h1>
              <p className="text-xs text-[#8B7E6A] tracking-widest uppercase">
                {t('header.artistTitle')}
              </p>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-10">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            ))}
            <LanguageSwitcher />
          </nav>

          {/* Mobile Menu Button - touch-friendly 44x44px target */}
          <button
            className="md:hidden icon-btn text-[#3D3831] hover:text-[#B8963E] -mr-2"
            onClick={() => setMobileMenuOpen(true)}
            aria-label={t('header.openMenu')}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-500 ${
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-[#2A2520]/60 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-[#FAF7F2] shadow-2xl transform transition-transform duration-500 safe-area-inset-right ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-4 xs:p-6 safe-area-inset-top">
            {/* Close Button */}
            <div className="flex justify-between items-center mb-8 xs:mb-12">
              <span className="text-sm text-[#8B7E6A] tracking-widest uppercase">
                {t('header.menu')}
              </span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="icon-btn text-[#3D3831] hover:text-[#B8963E] -mr-2"
                aria-label={t('header.closeMenu')}
              >
                <X size={24} />
              </button>
            </div>

            {/* Mobile Nav Items - touch-friendly with min 44px height */}
            <nav className="space-y-2">
              {navItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`block w-full text-left text-xl xs:text-2xl font-light tracking-wide transition-all duration-300 py-3 ${
                    activeSection === item.id
                      ? 'text-[#B8963E]'
                      : 'text-[#3D3831] hover:text-[#B8963E]'
                  }`}
                  style={{ animationDelay: `${index * 0.05}s`, minHeight: '44px' }}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Language Switcher */}
            <div className="mt-8 pt-8 border-t border-[#E8E2D9]">
              <LanguageSwitcher />
            </div>

            {/* Contact Info */}
            <div className="mt-8 pt-8 border-t border-[#E8E2D9]">
              <p className="text-sm text-[#8B7E6A] mb-2">{t('header.contact')}</p>
              <a
                href="mailto:info@irina-podlevskikh.ru"
                className="text-[#3D3831] underline-elegant"
              >
                info@irina-podlevskikh.ru
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
